import type { ReferralSummary } from '@common-reducers/backend-api/backend-api';

/** Display currency for the offer: the backend configures amounts per currency, USD first. */
const OFFER_CURRENCY = 'USD';

export function formatMoney(cents: number, currency: string): string {
  const whole = cents % 100 === 0;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2,
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(whole ? 0 : 2)} ${currency}`;
  }
}

function pick(amounts: Record<string, number> | undefined): { cents: number; currency: string } | undefined {
  if (!amounts) return undefined;
  const currency = OFFER_CURRENCY in amounts ? OFFER_CURRENCY : Object.keys(amounts)[0];
  return currency && amounts[currency] > 0 ? { cents: amounts[currency], currency } : undefined;
}

export interface OfferCopy {
  percent: number;
  cap?: string;
  credit?: string;
  holdDays: number;
}

export function offerCopy(offer: ReferralSummary['offer']): OfferCopy | undefined {
  if (!offer || !(offer.refereeDiscountPct > 0)) return undefined;
  const cap = pick(offer.refereeDiscountCapCents);
  const credit = pick(offer.referrerCreditCents);
  return {
    percent: offer.refereeDiscountPct,
    cap: cap && formatMoney(cap.cents, cap.currency),
    credit: credit && formatMoney(credit.cents, credit.currency),
    holdDays: offer.holdDays,
  };
}

/** "$10" / "$10 + €5", or undefined when nothing has been earned yet. */
export function earnedLabel(earnedCents: Record<string, number> | undefined): string | undefined {
  const parts = Object.entries(earnedCents ?? {})
    .filter(([, cents]) => cents > 0)
    .map(([currency, cents]) => formatMoney(cents, currency));
  return parts.length ? parts.join(' + ') : undefined;
}

/** A wa.me link with no number opens WhatsApp's own "send to…" picker. */
export function whatsappShareUrl(message: string): string {
  return `https://wa.me/?text=${encodeURIComponent(message)}`;
}
